import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://ims.holdthechild.net/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 500) {
      // Log server errors for debugging
      console.error('Server Error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Compliance Policy API functions
export const compliancePolicyApi = {
  // Get all compliance policies
  getAllPolicies: async () => {
    const response = await api.get('/compliance/policies');
    return response.data;
  },

  // Get a single policy by ID
  getPolicyById: async (id) => {
    const response = await api.get(`/compliance/policies/${id}`);
    return response.data;
  },

  // Create a new policy
  createPolicy: async (policyData) => {
    const response = await api.post('/compliance/policies', policyData);
    return response.data;
  },

  // Update a policy
  updatePolicy: async (id, policyData) => {
    const response = await api.put(`/compliance/policies/${id}`, policyData);
    return response.data;
  },

  // Delete a policy
  deletePolicy: async (id) => {
    const response = await api.delete(`/compliance/policies/${id}`);
    return response.data;
  },

  // Get policies applicable to a specific project
  getApplicablePolicies: async (projectId) => {
    const response = await api.get(`/compliance/policies/applicable/${projectId}`);
    return response.data;
  },
};

// Project Compliance API functions
export const projectComplianceApi = {
  // Get compliance for a specific project
  getProjectCompliance: async (projectId) => {
    const response = await api.get(`/compliance/projects/${projectId}`);
    return response.data;
  },

  // Toggle compliance status for an item
  toggleComplianceItem: async (projectId, itemId) => {
    const response = await api.put(`/compliance/projects/${projectId}/items/${itemId}/toggle`);
    return response.data;
  },

  // Add a custom compliance item
  addComplianceItem: async (projectId, itemData) => {
    const response = await api.post(`/compliance/projects/${projectId}/items`, itemData);
    return response.data;
  },

  // Update a custom compliance item
  updateComplianceItem: async (projectId, itemId, itemData) => {
    const response = await api.put(`/compliance/projects/${projectId}/items/${itemId}`, itemData);
    return response.data;
  },

  // Delete a custom compliance item
  deleteComplianceItem: async (projectId, itemId) => {
    const response = await api.delete(`/compliance/projects/${projectId}/items/${itemId}`);
    return response.data;
  },

  // Add approval to a compliance item
  addComplianceApproval: async (projectId, itemId, approvalData) => {
    const response = await api.post(`/compliance/projects/${projectId}/items/${itemId}/approvals`, approvalData);
    return response.data;
  },

  // Refresh compliance items for a project
  refreshComplianceItems: async (projectId) => {
    const response = await api.post(`/compliance/projects/${projectId}/refresh`);
    return response.data;
  },

  // Get compliance metrics for all projects
  getComplianceMetrics: async () => {
    const response = await api.get('/compliance/metrics');
    return response.data;
  },

  // Get compliance summary for all projects
  getComplianceSummary: async () => {
    const response = await api.get('/compliance/summary');
    return response.data;
  },
};

export default api;